import Modal from "components/DesignSystem/Modal/Modal";
import { Stepper } from "components/DesignSystem/Stepper/Stepper";
import { SwitchCase } from "components/DesignSystem/SwitchCase/SwitchCase";
import { ArrowRight } from "components/icons/ArrowRight";
import { STEPS, STEPS_BREADCRUMBS, STEPS_TYPE } from "constants/coaImportSteps";
import { ModalControl } from "hooks/useModal";
import { useWindowSize } from "hooks/useWindowSize";
import { createContext, useContext, useState } from "react";
import { AddDetailsStep } from "./AddDetailsStep";
import { ImportCOAFileUpload } from "./ImportCOAFileUpload";
import { MapCategoriesStep } from "./MapCategoriesStep";
import { MapDataConnectionsStep } from "./MapDataConnectionsStep";
import { ReviewStep } from "./ReviewStep";

type ImportCOAProps = {
  entityId: string;
} & ModalControl;

interface ImportCOAContextType {
  currentStep: STEPS_TYPE;
  setCurrentStep: (step: STEPS_TYPE) => void;
  coaCSV: File | null;
  setCoaCSV: (file: File | null) => void;
  importProcessId: string | null;
  setImportProcessId: (id: string | null) => void;
  entityId: string;
  onNext: () => void;
  onStepChange: (step: STEPS_TYPE) => void;
}

const ImportCOAContext = createContext<ImportCOAContextType | undefined>(
  undefined
);

export const useImportCOA = () => {
  const context = useContext(ImportCOAContext);
  if (context === undefined) {
    throw new Error("useImportCOA must be used within an ImportCOAProvider");
  }
  return context;
};

interface ImportCOAProviderProps {
  children: React.ReactNode;
  entityId: string;
  onClose: () => void;
}

const ImportCOAProvider = ({
  children,
  entityId,
  onClose,
}: ImportCOAProviderProps) => {
  const [coaCSV, setCoaCSV] = useState<File | null>(null);
  const [currentStep, setCurrentStep] = useState<STEPS_TYPE>(STEPS.IMPORT_COA);
  const [importProcessId, setImportProcessId] = useState<string | null>(null);

  const onNext = () => {
    if (currentStep === STEPS.REVIEW) {
      onClose();
      setCurrentStep(STEPS.IMPORT_COA);
      setCoaCSV(null);
      setImportProcessId(null);
    } else {
      const currentIndex = Object.values(STEPS).indexOf(currentStep);
      const nextStep = Object.values(STEPS)[currentIndex + 1];
      setCurrentStep(nextStep);
    }
  };

  const onStepChange = (step: STEPS_TYPE) => {
    setCurrentStep(step);
  };

  const contextValue: ImportCOAContextType = {
    currentStep,
    setCurrentStep,
    coaCSV,
    setCoaCSV,
    importProcessId,
    setImportProcessId,
    entityId,
    onNext,
    onStepChange,
  };

  return (
    <ImportCOAContext.Provider value={contextValue}>
      {children}
    </ImportCOAContext.Provider>
  );
};

const ImportStepper = () => {
  const { currentStep, onStepChange } = useImportCOA();
  const { isMobile } = useWindowSize();

  // Five steps can't fit a phone header, so phones show just the current step.
  if (isMobile) {
    const currentIndex = Object.values(STEPS).indexOf(currentStep);
    const currentStepName = STEPS_BREADCRUMBS.find(
      ({ step }) => step === currentStep
    )?.name;

    return (
      <span className="t-text-body-sm t-text-purple">
        Step {currentIndex + 1} of {STEPS_BREADCRUMBS.length} ·{" "}
        {currentStepName}
      </span>
    );
  }

  return (
    <Stepper size="xs" direction="horizontal" className="!t-gap-0">
      {STEPS_BREADCRUMBS?.map(({ name, step, no }) => (
        <Stepper.Step
          key={step}
          step={no}
          isActive={
            Object.values(STEPS).indexOf(currentStep) >=
            Object.values(STEPS).indexOf(step)
          }
          onClick={() => onStepChange(step)}
          clickable={
            Object.values(STEPS).indexOf(currentStep) >
            Object.values(STEPS).indexOf(step)
          }
        >
          <div className="t-flex t-items-center">
            {name}
            {step !== STEPS.REVIEW && <ArrowRight color="currentColor" />}
          </div>
        </Stepper.Step>
      ))}
    </Stepper>
  );
};

const ImportCOASteps = () => {
  const {
    currentStep,
    coaCSV,
    onNext,
    importProcessId,
    entityId,
    setImportProcessId,
    setCoaCSV,
  } = useImportCOA();

  return (
    <SwitchCase
      value={currentStep}
      cases={{
        [STEPS.IMPORT_COA]: (
          <ImportCOAFileUpload
            file={coaCSV}
            onFileUpload={setCoaCSV}
            onNext={onNext}
          />
        ),
        [STEPS.ADD_DETAILS]: coaCSV && (
          <AddDetailsStep
            onNext={onNext}
            coaCSV={coaCSV}
            entityId={entityId}
            setImportProcessId={setImportProcessId}
          />
        ),
        [STEPS.MAP_DATA_CONNECTIONS]: importProcessId && (
          <MapDataConnectionsStep
            importProcessId={importProcessId}
            entityId={entityId}
            onNext={onNext}
          />
        ),
        [STEPS.MAP_CATEGORIES]: importProcessId && (
          <MapCategoriesStep
            importProcessId={importProcessId}
            entityId={entityId}
            onNext={onNext}
          />
        ),
        [STEPS.REVIEW]: importProcessId && (
          <ReviewStep
            importProcessId={importProcessId}
            entityId={entityId}
            onNext={onNext}
          />
        ),
      }}
    />
  );
};

export const ImportCOAHeader = () => {
  return (
    <Modal.Header className="sm:t-pl-4">
      <div className="t-flex t-flex-col t-gap-2">
        <Modal.Title>Import Chart of Accounts</Modal.Title>
        <ImportStepper />
      </div>
      <Modal.Close />
    </Modal.Header>
  );
};

export const ImportCOA = ({ isOpen, close, entityId }: ImportCOAProps) => {
  return (
    <Modal.Root open={isOpen} onOpenChange={close} modal={false}>
      <ImportCOAProvider entityId={entityId} onClose={close}>
        <ImportCOASteps />
      </ImportCOAProvider>
    </Modal.Root>
  );
};
